/**
 * QuestionViewModal — read-only question detail modal for admin pages.
 *
 * Replaces cracking open the edit modal just to read a question.
 * Works for both oral and written question kinds.
 *
 * Props:
 *   question         — raw question document
 *   kind             — 'oral' | 'written'
 *   open             — bool
 *   onClose          — fn
 *   onEdit           — fn  (transitions straight into edit modal)
 *   helpers          — { getFunctionName(id)→str, getTopicName(id)→str,
 *                        getImageUrl(img)→str, getReviewCategory(id)→obj }
 *   reviewEnabled    — bool
 *   paperGrade       — string (written: from paper)
 *   paperFunction    — string (written: from paper)
 */
import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../ui/tooltip';
import {
  Eye, Edit, Copy, Tag,
  CheckCircle2, Clock, BookOpen, Video, ZoomIn,
  ClipboardPen, Hash, MapPin,
} from 'lucide-react';
import { toast } from 'sonner';
import ReviewBadge from './ReviewBadge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/* ── helpers ─────────────────────────────────────── */
const resolveImageUrl = (img) => {
  if (!img?.url) return '';
  return img.url.startsWith('http') ? img.url : `${BACKEND_URL}${img.url}`;
};

const VideoEmbed = ({ url }) => {
  if (!url?.trim()) return null;

  // Raw iframe embed code pasted directly
  if (url.trim().startsWith('<')) {
    return (
      <div
        className="w-full aspect-video rounded-lg overflow-hidden border"
        dangerouslySetInnerHTML={{ __html: url }}
      />
    );
  }

  // YouTube — extract ID for canonical embed URL
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/);
  if (yt) {
    return (
      <iframe
        src={`https://www.youtube.com/embed/${yt[1]}`}
        title="Video"
        className="w-full aspect-video rounded-lg border"
        allowFullScreen
      />
    );
  }

  // Dadan (dadan.io, video.helmtorq.com, or any other normalised embed URL)
  // The backend parser guarantees only YouTube and Dadan URLs are stored,
  // so anything reaching here is safe to render as an iframe.
  return (
    <iframe
      src={url}
      title="Video"
      className="w-full aspect-video rounded-lg border"
      allowFullScreen
      referrerPolicy="strict-origin-when-cross-origin"
      allow="fullscreen; autoplay; picture-in-picture"
    />
  );
};

/* ── image grid ──────────────────────────────────── */
const ImageGrid = ({ images = [], label, testId }) => {
  if (!images.length) return null;
  return (
    <div data-testid={testId}>
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">{label}</p>
      <div className="flex flex-wrap gap-2">
        {images.map((img, i) => {
          const src = resolveImageUrl(img);
          return (
            <div key={img.id || i} className="relative group cursor-pointer" onClick={() => window.open(src, '_blank')}>
              <img
                src={src}
                alt={`${label} ${i + 1}`}
                className="h-24 w-auto rounded-lg border object-cover hover:opacity-90 transition-opacity"
              />
              <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 rounded-lg transition-opacity">
                <ZoomIn className="w-5 h-5 text-white" />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ── main component ──────────────────────────────── */
const QuestionViewModal = ({
  question,
  kind = 'oral',
  open,
  onClose,
  onEdit,
  helpers = {},
  reviewEnabled = false,
  paperGrade,
  paperFunction,
}) => {
  if (!question) return null;

  const {
    getFunctionName = () => null,
    getTopicName    = () => null,
    getReviewCategory = () => null,
  } = helpers;

  /* resolve topic IDs from either legacy or new field */
  const topicIds = question.topic_ids?.length > 0
    ? question.topic_ids
    : question.topic_id ? [question.topic_id] : [];

  const reviewCategory = getReviewCategory(question.review_category_id);

  const copyId = () => {
    navigator.clipboard?.writeText(question.id);
    toast.success('Question ID copied');
  };

  /* ── sittings history (oral) ── */
  const sittings = question.asked_by_history || [];

  const grade        = kind === 'oral' ? question.grade : paperGrade;
  const functionName = kind === 'oral'
    ? getFunctionName(question.function_id)
    : paperFunction;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="sm:max-w-[700px] w-[92vw] max-h-[88vh] overflow-y-auto"
        data-testid={`question-view-modal-${kind}`}
      >
        {/* ── Header ── */}
        <DialogHeader className="pb-0">
          <DialogTitle className="flex items-center gap-2 text-base font-semibold">
            <Eye className="w-4 h-4 text-[#0A7EA4]" />
            Question
            <Badge
              variant="outline"
              className={`text-[10px] px-1.5 py-0 h-4 ${
                kind === 'oral'
                  ? 'text-[#0A7EA4] border-[#0A7EA4]'
                  : 'text-purple-700 border-purple-400'
              }`}
            >
              {kind === 'oral' ? 'Oral' : 'Written'}
            </Badge>
          </DialogTitle>

          {/* ID + Review chip row */}
          <div className="mt-1 flex items-center gap-2 flex-wrap text-xs text-gray-500">
            <Hash className="w-3 h-3" />
            <code className="bg-gray-100 px-1.5 py-0.5 rounded font-mono text-[11px] text-gray-800 select-all">
              {question.id}
            </code>
            <button
              onClick={copyId}
              className="text-blue-600 hover:text-blue-800 hover:underline"
              title="Copy ID"
              data-testid={`view-modal-copy-id-${kind}`}
            >
              copy
            </button>
            {reviewEnabled && question.needs_review && (
              <ReviewBadge
                question={question}
                category={reviewCategory}
                dataTestId={`view-modal-review-badge-${kind}`}
              />
            )}
          </div>
        </DialogHeader>

        <Separator className="my-3" />

        {/* ── Metadata tags ── */}
        <div className="flex flex-wrap gap-1.5" data-testid={`view-modal-meta-${kind}`}>
          {grade && (
            <Badge className="bg-[#0A7EA4]">{grade}</Badge>
          )}
          {kind === 'oral' && question.mmd_location && (
            <Badge className="bg-[#14B8A6]">
              <MapPin className="w-3 h-3 mr-1" />{question.mmd_location}
            </Badge>
          )}
          {functionName && (
            <Badge className="bg-orange-500">{functionName}</Badge>
          )}
          {kind === 'oral' && question.status && (
            <Badge variant="outline">{question.status}</Badge>
          )}
          {/* Answered / Not Answered */}
          {(kind === 'oral' ? question.has_answer : question.is_answered) ? (
            <Badge className="bg-green-600">
              <CheckCircle2 className="w-3 h-3 mr-1" />Answered
            </Badge>
          ) : (
            <Badge variant="outline" className="text-amber-600 border-amber-300">
              <Clock className="w-3 h-3 mr-1" />Not Answered
            </Badge>
          )}
          {kind === 'oral' && question.is_decider && (
            <Badge className="bg-[#FF6B6B]">Decider</Badge>
          )}
          {kind === 'oral' && question.is_marq_recommended && (
            <Badge className="bg-purple-600">MARQ Recommended</Badge>
          )}
          {kind === 'oral' && question.asked > 1 && (
            <Badge className="bg-orange-600">Asked {question.asked}x</Badge>
          )}
          {kind === 'written' && question.asked_count > 1 && (
            <Badge className="bg-orange-600">Asked {question.asked_count}x</Badge>
          )}          {kind === 'written' && question.marks && (
            <Badge variant="outline">{question.marks} marks</Badge>
          )}
          {/* Topics */}
          {topicIds.length > 0 ? (
            topicIds
              .map(tid => getTopicName(tid))
              .filter(Boolean)
              .map((name, i) => (
                <Badge key={i} className="bg-[#8B5CF6]">
                  <Tag className="w-3 h-3 mr-1" />{name}
                </Badge>
              ))
          ) : (
            <Badge variant="outline" className="text-gray-500">No Topic</Badge>
          )}
        </div>

        <Separator className="my-3" />

        {/* ── Question text ── */}
        <section data-testid={`view-modal-question-text-${kind}`}>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-2 flex items-center gap-1">
            <BookOpen className="w-3.5 h-3.5" /> Question
          </p>
          <p className="text-gray-900 leading-relaxed whitespace-pre-wrap text-sm">
            {question.question_text}
          </p>
          {/* Question images — written only */}
          {kind === 'written' && (
            <div className="mt-3">
              <ImageGrid
                images={question.question_images || []}
                label="Question images"
                testId="view-modal-question-images-written"
              />
            </div>
          )}
        </section>

        {/* ── Answer ── */}
        {(kind === 'oral' ? question.has_answer : question.is_answered) ? (
          <>
            <Separator className="my-3" />
            <section data-testid={`view-modal-answer-${kind}`}>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-2 flex items-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5 text-green-600" /> Answer
              </p>
              {(question.answer_text || question.answer) ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">
                    {question.answer_text || question.answer}
                  </p>
                </div>
              ) : (
                <p className="text-sm text-gray-400 italic">Answer text not recorded.</p>
              )}

              {/* Answer images */}
              {(question.answer_images?.length > 0) && (
                <div className="mt-3">
                  <ImageGrid
                    images={question.answer_images}
                    label="Answer images"
                    testId={`view-modal-answer-images-${kind}`}
                  />
                </div>
              )}

              {/* Video */}
              {question.video_url && (
                <div className="mt-4" data-testid={`view-modal-video-${kind}`}>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-2 flex items-center gap-1">
                    <Video className="w-3.5 h-3.5" /> Video
                  </p>
                  <VideoEmbed url={question.video_url} />
                </div>
              )}
            </section>
          </>
        ) : (
          <>
            <Separator className="my-3" />
            <div
              className="text-center py-5 text-sm text-gray-400 bg-gray-50 rounded-lg"
              data-testid={`view-modal-no-answer-${kind}`}
            >
              No answer recorded yet.
            </div>
          </>
        )}

        {/* ── Review remarks (expanded — if flagged) ── */}
        {reviewEnabled && question.needs_review && question.review_remarks && (
          <>
            <Separator className="my-3" />
            <section data-testid={`view-modal-review-remarks-${kind}`}>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-500 mb-2 flex items-center gap-1">
                <ClipboardPen className="w-3.5 h-3.5" /> Review Remarks
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-900 leading-relaxed whitespace-pre-wrap">
                  {question.review_remarks}
                </p>
              </div>
            </section>
          </>
        )}

        {/* ── Footer ── */}
        <DialogFooter className="mt-4 gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            data-testid={`view-modal-close-${kind}`}
          >
            Close
          </Button>
          {onEdit && (
            <Button
              size="sm"
              onClick={() => { onClose(); setTimeout(onEdit, 80); }}
              className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white"
              data-testid={`view-modal-edit-btn-${kind}`}
            >
              <Edit className="w-3.5 h-3.5 mr-1.5" />
              Edit Question
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default QuestionViewModal;
